import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { APIURL } from "../../../config";
import moment from "moment";
import dayjs from 'dayjs';
import { TextField, Button, Box, Link, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Snackbar, Alert, IconButton, Autocomplete, CircularProgress, Chip } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import Upload from "./Upload";
import MonthlyTool from "../../../container/SupportCoordinator/MonthlyMonitoring/MonthlyTool/MonthlyTool";
import QuarterlyAnnualTool from "../../../container/SupportCoordinator/MonthlyMonitoring/QuarterlyAnnualTool/QuarterlyAnnualTool";
import { DataGridPro, useGridApiRef, ToolbarButton } from '@mui/x-data-grid-pro';
import ControlledDataGrid from "../../DataGrid/ControlledDataGrid";
import DefaultToolbar from "src/components/DataGrid/DefaultToolbar";
import SaveViewButton from "src/components/DataGrid/SaveViewButton";
import { AnalyticsBrowser } from '@segment/analytics-next'
import { SEGMENTAPIKEY } from '../../../config';
import { Form } from '@formio/react';
import { convertTime } from "src/utils/time";

const analytics = AnalyticsBrowser.load({ writeKey: SEGMENTAPIKEY })

const shortcutsItems = [
  {
    label: 'This Week',
    getValue: () => {
      const today = dayjs();
      return [today.startOf('week'), today.endOf('week')];
    },
  },
  {
    label: 'Last Week',
    getValue: () => {
      const today = dayjs();
      const prevWeek = today.subtract(7, 'day');
      return [prevWeek.startOf('week'), prevWeek.endOf('week')];
    },
  },
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = dayjs();
      return [today.subtract(7, 'day'), today];
    },
  },
  {
    label: 'Current Month',
    getValue: () => {
      const today = dayjs();
      return [today.startOf('month'), today.endOf('month')];
    },
  },
  {
    label: 'Last Month',
    getValue: () => {
      const today = dayjs();
      const lastMonth = today.subtract(1, 'month');
      return [lastMonth.startOf('month'), lastMonth.endOf('month')];
    },
  },
];

// Non-SC rows can only be filed once every matching task has ended, so the latest
// tas_end_date across the matching set is the guard. Task end dates arrive as ISO
// strings; normalize to YYYY-MM-DD so a plain string compare orders them correctly.
const getLatestTaskEndDate = (tasks) => (tasks || [])
  .map((t) => t && t.tas_end_date)
  .filter((d) => !!d && moment.utc(d).isValid())
  .map((d) => moment.utc(d).format('YYYY-MM-DD'))
  .sort()
  .pop() || null;

const isFutureDate = (date) => !!date && date > moment().format('YYYY-MM-DD');

export default function PreAuth(props) {
  const [gridId, setGridId] = useState(props.tabKey === 'changeCapture' ? 62 : 49);
  const apiRef = useGridApiRef();
  const ref = useRef();
  const dateRangeView = props.user.comp_settings.find(s => s.feature_id === 70)?.feature_value || 'month';

  const [claimsList, setClaimsList] = useState([]);
  const [formattedRows, setFormattedRows] = useState([]);
  const [fromDate, setFromDate] = useState(() => dateRangeView === 'week' ? dayjs().startOf('week').toDate() : dayjs().startOf('month').toDate());
  const [toDate, setToDate] = useState(() => dateRangeView === 'week' ? dayjs().endOf('week').toDate() : dayjs().endOf('month').toDate());
  const [isLoading, setLoading] = useState(true);
  const [isAPILoading, setAPILoading] = useState(false);
  const [csvAdd, setCsvAdd] = useState(false);
  const [updateData, setUpdateData] = useState(false);
  const [selectionModel, setSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [selectValidClaims, setSelectValidClaims] = useState(false);
  const [expectedAmountTotal, setExpectedAmountTotal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [mt, setMT] = useState({
    type: '',
    data: null,
  });
  const [showMT, setShowMT] = useState(false);
  const handleCloseMT = () => setShowMT(false);
  const [density, setDensity] = useState("standard");
  const [initialSettings, setInitialSettings] = useState({});
  const [orderedColumns, setOrderedColumns] = useState(props.columns ? props.columns.reduce((acc, col) => ([...acc, col.field]), []) : []);
  const [colVisModel, setColVisModel] = useState({
    // Hide columns status and traderName, the other columns will remain visible
    primaryID: false,
  });
  const [customerList, setCustomerList] = useState([]);
  const [newPAFormOpen, setNewPAFormOpen] = useState(false);
  const [addingPreAuth, setAddingPreAuth] = useState(false);
  const [PAFormData, setPAFormData] = useState({
    DDDID: '',
    memberMedicaid: '',
    providerMedicaid: '',
    priorAuth: '',
    procedureCode: '',
    startDate: moment(),
    endDate: moment(),
    authUnits: '',
    authAmount: '',
  });
  const [selectedTaskID, setSelectedTaskID] = useState(null);
  const [taskForms, setTaskForms] = useState([]);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [editNote, setEditNote] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [claimStatus, setClaimStatus] = useState(null);
  const [claimStatusLoading, setClaimStatusLoading] = useState(false);

  const primaryBusinessType = props.user?.comp_settings.find(setting => setting.feature_value === 'primaryBusinessType')
    ? props.user.comp_settings.filter((setting) => setting.feature_value === "primaryBusinessType")[0].feature_id
    : 101;

  const logToSegment = () => {
    let page = String(window.location.href).split("/").pop()

    if (page.includes("fieldworker") || page === "") {
      page = "Home";
    } else {
      page = String(page).charAt(0).toUpperCase() + String(page).toLowerCase().slice(1);
    }

    analytics.page(page + ` : ${props.tabKey === 'changeCapture' ? 'Change Capture' : 'Prior Authorization'}`, {
      title: page + ` : ${props.tabKey === 'changeCapture' ? 'Change Capture' : 'Prior Authorization'}`,
      path: page + ` : ${props.tabKey === 'changeCapture' ? 'Change Capture' : 'Prior Authorization'}`,
    }, {
      userId: String(props.user.emp_id),
    })
  };

  useEffect(() => {
    logToSegment()
  }, []);

  useEffect(() => {
    if (primaryBusinessType) {
      fetchData();
    }
  }, [csvAdd, addingPreAuth, fromDate, editRow, updateData]);

  const fetchData = async () => {
    setLoading(true);
    setSelectionModel({ type: 'include', ids: new Set() });
    setSelectValidClaims(false);

    const response = await axios.get(`${APIURL}/preAuth`, {
      params: {
        companyID: props.comp_id,
        primaryBusinessType: primaryBusinessType,
        startDate: moment(fromDate).format('YYYY-MM-DD'),
        endDate: moment(toDate).format('YYYY-MM-DD'),
      }
    });

    setClaimsList(props.tabKey === 'changeCapture' ? response.data.rows.filter(row => row.ChargeIdentified !== '-') : response.data.rows);

    const transformedRows = response.data.rows.map((item, index) => {
      if (primaryBusinessType === 101) {
        // Support Coordination format
        return {
          hierarchy: [item.ChargeIdentified, item.cus_name + ': ' + moment(item.startDate).utc().format('MM/DD') + ' - ' + moment(item.endDate).utc().format('MM/DD') + ' (' + index + ')'],
          id: index,
          sourceRow: item,
          primaryID: item.primaryID || null,
          DDDID: Array.isArray(item.DDDID) ? item.DDDID[0] : item.DDDID,
          PatientName: item.cus_name,
          DateOfBirth: item.dob ? moment(item.dob).utc().format("L") : '',
          Gender: item.gender || '',
          DDD_tier: item.DDD_tier || '',
          memberMedicaid: item.memberMedicaid,
          priorAuth: item.priorAuth,
          procedureCode: item.procedureCode,
          procedureCodeModifier: item.procedureCodeModifier || '',
          startDate: moment(item.startDate).isValid() ? moment(item.startDate).utc().format("L") : '',
          endDate: moment(item.endDate).isValid() ? moment(item.endDate).utc().format("L") : '',
          DiagnosisCode: item.PrimaryDiagnosisCode,
          authUnits: item.authUnits,
          authAmount: item.authAmount ? item.authAmount.toFixed(2) : '',
          Status: item.tas_preAuth_id ? 'Sent to Claims' : '',
          tas_title: item.tas_title,
          tas_type: item.tas_type,
          tas_start_date: item.tas_start_date ? moment(item.tas_start_date).utc().format("L") : '',
          taskStatus: item.tas_title ? 'Task Matched' : '',
          mmt: item.tas_id,
          errCode: item.errCode === 'None' ? '' : item.errCode,
          errDescription: item.errDescription === 'Null' ? '' : item.errDescription,
          tas_id: item.tas_id,
          task_id: item.tas_id,
          mt_status: item.tas_mm_status,
          billingNoteId: item.billing_note_id,
          billingNote: item.billing_note,
          ChargeIdentified: item.ChargeIdentified
        };
      } else {
        // Service Provider format
        const taskList = item.tasks;

        return {
          hierarchy: [item.ChargeIdentified, item.cus_name + ': ' + moment(item.startDate).utc().format('MM/DD') + ' - ' + moment(item.endDate).utc().format('MM/DD') + ' (' + index + ')'],
          id: index,
          sourceRow: item,
          primaryID: item.primaryID || null,
          DDDID: Array.isArray(item.DDDID) ? item.DDDID[0] : item.DDDID,
          PatientName: item.cus_name,
          DateOfBirth: item.dob ? moment(item.dob).utc().format("L") : '',
          Gender: item.gender || '',
          DDD_tier: item.DDD_tier || '',
          projectEndsOn: item.pro_ends_on ? moment(item.pro_ends_on).utc().format("L") : '',
          memberMedicaid: item.memberMedicaid,
          priorAuth: item.priorAuth,
          procedureCode: item.procedureCode,
          procedureCodeModifier: item.procedureCodeModifier || '',
          startDate: item.startDate ? moment(item.startDate).utc().format("L") : '',
          endDate: item.endDate ? moment(item.endDate).utc().format("L") : '',
          latestTaskEndDate: getLatestTaskEndDate(taskList),
          DiagnosisCode: item.PrimaryDiagnosisCode,
          authUnits: item.authUnits,
          authAmount: item.authAmount ? item.authAmount.toFixed(2) : '',
          billingRate: !!taskList.length ? (taskList[0].rate).toFixed(2) : null,
          expectedAmount: !!taskList.length ? (taskList[0].rate * Math.round(item.cumulativeUnits || 0)).toFixed(2) : null,
          Status: !!taskList.length ? (!!taskList[0].tas_preAuth_id ? 'Sent to Claims' : '') : !!item.tas_preAuth_id ? 'Sent to Claims' : '',
          taskStatus: item.taskIds ? 'Tasks Matched' : '',
          tas_ids: item.taskIds,
          tas_type: item.taskType ? item.taskType : (!!taskList.length ? taskList[0].tas_type : null),
          cumulativeUnits: Math.round(item.cumulativeUnits || 0),
          totalRemainingUnits: Math.round(item.cumulativeRemainingUnits || 0),
          remainingUnits: item.authUnits - Math.round(item.cumulativeUnits || 0),
          errCode: item.errCode === 'None' ? '' : item.errCode,
          errDescription: item.errDescription === 'Null' ? '' : item.errDescription,
          isNewPlan: item.isNewPlan === 'Null' ? '' : item.isNewPlan,
          serviceType: item.serviceType === 'Null' ? '' : item.serviceType,
          billingNoteId: item.billing_note_id,
          billingNote: item.billing_note,
          ChargeIdentified: item.ChargeIdentified
        };
      }
    });

    setFormattedRows(props.tabKey === 'changeCapture' ? transformedRows.filter(row => row.ChargeIdentified !== '-') : transformedRows);
    setLoading(false);
  };

  useEffect(() => {
    fetchDatagridSettings();
  }, [updateData]);

  const fetchDatagridSettings = async () => {
    let getGridSettings = await axios.get(`${APIURL}/datagrid`, { params: { user_id: props.user.emp_id, report_id: gridId } });
    if (getGridSettings.data.length > 0) {
      let settings = JSON.parse(getGridSettings.data[0].settings);

      setInitialSettings({ ...settings });
      setColVisModel({ ...colVisModel, ...settings.columnVisibilityModel });
      setDensity(settings.density ? settings.density : "standard");
      setOrderedColumns(settings.columns ? settings.columns.orderedFields : []);
    } else {
      let settings = props.initialState ? props.initialState : {};
      setInitialSettings({ ...settings });

      settings = settings ? JSON.stringify(settings) : JSON.stringify({});
      let saveGridSettings = await axios.post(`${APIURL}/datagrid`, { user_id: props.user.emp_id, report_id: gridId, settings: settings });
    }
  };

  const sendToClaims = async () => {
    setAPILoading(true);
    if (selectionModel.ids.size !== 0) {
      const selectedEntries = formattedRows
        .filter(r => selectionModel.ids.has(r.id))
        .map(r => r.sourceRow)
        .filter(Boolean);

      try {
        const response = await axios.post(APIURL + "/claims", {
          entries: selectedEntries,
          comp_id: props.user.comp_id,
          emp_id: props.user.emp_id,
          emp_name: props.user.emp_name,
          primaryBusinessType: primaryBusinessType
        });

        const claimsCreated = response.data?.claimsCreated;

        if (response.data?.success === false) {
          setAlertMessage(response.data?.message || 'The selected entries could not be sent to Claims.');
          setAlertType('error');
        } else if (claimsCreated !== undefined && claimsCreated !== selectedEntries.length) {
          setAlertMessage(`Only ${claimsCreated} of ${selectedEntries.length} entries were sent to Claims.`);
          setAlertType('error');
        } else {
          setAlertMessage('Entries successfully sent to Claims.');
          setAlertType('success');
        }
      } catch (error) {
        setAlertMessage(error.response?.data?.message || error.message);
        setAlertType('error');
      }

      setUpdateData(!updateData);
      setSelectValidClaims(false);
      setSelectionModel({ type: 'include', ids: new Set() });
    }
    setShowModal(!showModal);
    setAPILoading(false);
  };

  useEffect(() => {
    if (selectValidClaims) {
      let readyToSubmitRows = formattedRows
        .filter((r) => (primaryBusinessType === 101 ? ((r.mt_status === "Approved" || r.ChargeIdentified === 'Customer added') && r.priorAuth) : (r.tas_ids && r.priorAuth && !isFutureDate(r.latestTaskEndDate))) && r.Status !== 'Sent to Claims' && moment(r.startDate).utc().month() === moment(fromDate).month() && moment(r.startDate).utc().year() === moment(fromDate).year() && r.PatientName)
        .map((r) => r.id);
      setSelectionModel({ type: 'include', ids: new Set(readyToSubmitRows) });

      const selectedRows = formattedRows.filter(r => readyToSubmitRows.includes(r.id));
      setExpectedAmountTotal(getExpectedAmountTotal(selectedRows));
    }
    else {
      setSelectionModel({ type: 'include', ids: new Set() });
    }
  }, [selectValidClaims]);

  useEffect(() => {
    if (mt.data)
      setShowMT(true);
  }, [mt]);

  const renderCSVModal = () => {
    return (
      <Upload
        showUpload={csvAdd}
        setCsvAdd={setCsvAdd}
        toggleCSVAdd={() => setCsvAdd(!csvAdd)}
        user={props.user}
      />
    );
  };

  useEffect(() => {
    if (newPAFormOpen) {
      axios.get(APIURL + "/customers/v2/associated", {
        params: {
          company_id: props.user.comp_id,
          employee_id: props.user.emp_id,
          type: props.user.permissions.AdminLevelCustomers ? "Admin" : "Manager"
        },
      })
        .then((response) => {
          setCustomerList(response.data.filter((cus) => cus.cus_status === 'Active'));
        })
        .catch((error) => {
          console.log(error);
          setCustomerList([]);
        });
    }
  }, [newPAFormOpen]);

  const addPreAuthForm = () => {
    return (
      <div>
        <Dialog open={newPAFormOpen} onClose={handleSnackbarAlertClose}>
          <DialogTitle sx={{ width: '100%', textAlign: 'center', borderBottom: '1px solid lightgray' }}>
            Add New Prior Authorization
          </DialogTitle>
          <DialogContent>
            <Autocomplete
              options={customerList}
              value={customerList.find(cus => cus.DDDID === PAFormData.DDDID) || null}
              getOptionLabel={(option) => option?.cus_name || ''}
              isOptionEqualToValue={(option, value) => option.DDDID === value.DDDID}
              onChange={(event, value) => { setPAFormData({ ...PAFormData, DDDID: value ? value.DDDID : '' }); }}
              renderOption={(props, option) => (
                <li {...props} key={option.cus_id}>
                  {option.cus_name}
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Select Customer" error={!PAFormData.DDDID} />}
              sx={{ marginTop: 2 }}
            />
            <TextField
              label="Member Medicaid"
              value={PAFormData.memberMedicaid}
              sx={{ marginTop: 2 }}
              onChange={(event) => setPAFormData({ ...PAFormData, memberMedicaid: event.target.value })}
              fullWidth
              error={!PAFormData.memberMedicaid}
            />
            <TextField
              label="Provider Medicaid"
              value={PAFormData.providerMedicaid}
              sx={{ marginTop: 2 }}
              onChange={(event) => setPAFormData({ ...PAFormData, providerMedicaid: event.target.value })}
              fullWidth
              error={!PAFormData.providerMedicaid}
            />
            <TextField
              label="Prior Authorization"
              value={PAFormData.priorAuth}
              sx={{ marginTop: 2 }}
              onChange={(event) => setPAFormData({ ...PAFormData, priorAuth: event.target.value })}
              fullWidth
              error={!PAFormData.priorAuth}
            />
            <TextField
              label="Procedure Code"
              value={PAFormData.procedureCode}
              sx={{ marginTop: 2 }}
              onChange={(event) => setPAFormData({ ...PAFormData, procedureCode: event.target.value })}
              fullWidth
              error={!PAFormData.procedureCode}
            />
            <Grid sx={{ marginTop: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Start Date"
                  value={PAFormData.startDate}
                  onChange={(newValue) => setPAFormData({ ...PAFormData, startDate: newValue })}
                  sx={{ width: '49%', marginRight: 1 }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="End Date"
                  value={PAFormData.endDate}
                  onChange={(newValue) => setPAFormData({ ...PAFormData, endDate: newValue })}
                  sx={{ width: '49%' }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, marginBottom: 2 }}>
              <TextField
                label="Authorized Units"
                type='number'
                value={PAFormData.authUnits}
                sx={{ width: '49%', marginRight: 1 }}
                onChange={(event) => setPAFormData({ ...PAFormData, authUnits: event.target.value })}
                fullWidth
                error={!PAFormData.authUnits}
              />
              <TextField
                label="Authorized Amount"
                type='number'
                value={PAFormData.authAmount}
                sx={{ width: '49%' }}
                onChange={(event) => setPAFormData({ ...PAFormData, authAmount: event.target.value })}
                fullWidth
                error={!PAFormData.authAmount}
              />
            </Grid>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              sx={{ marginTop: 2 }}
              onClick={handleAddPreAuth}
              disabled={
                addingPreAuth ||
                !PAFormData.DDDID ||
                !PAFormData.memberMedicaid ||
                !PAFormData.providerMedicaid ||
                !PAFormData.priorAuth ||
                !PAFormData.procedureCode ||
                !PAFormData.startDate ||
                !PAFormData.endDate ||
                !PAFormData.authUnits ||
                !PAFormData.authAmount
              }
            >
              Add Prior Authorization
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const handleAddPreAuth = async () => {
    setAddingPreAuth(true);

    await axios
      .post(APIURL + `/preAuth/addPA`, {
        DDDID: PAFormData.DDDID,
        memberMedicaid: PAFormData.memberMedicaid,
        providerMedicaid: PAFormData.providerMedicaid,
        priorAuth: PAFormData.priorAuth,
        procedureCode: PAFormData.procedureCode.slice(0, 5),
        procedureCodeModifier: PAFormData.procedureCode.slice(5).replaceAll(":", ''),
        startDate: moment(PAFormData.startDate).format('YYYY-MM-DD'),
        endDate: moment(PAFormData.endDate).format('YYYY-MM-DD'),
        authUnits: PAFormData.authUnits,
        authAmount: PAFormData.authAmount,
        companyId: props.comp_id,
        uploadedBy: props.user.emp_id,
      })
      .then(function (response) {
        setAlertMessage('Prior Authorization saved successfully!');
        setAlertType('success');
      })
      .catch(function (error) {
        console.log(error);
        setAlertMessage('An error occurred while saving the Prior Authorization!');
        setAlertType('error');
      });
  };

  const SnackbarComponent = () => {
    return (
      <div>
        <Snackbar open={alertMessage ? true : false} autoHideDuration={2000} onClose={handleSnackbarAlertClose}>
          <Alert severity={alertType} elevation={6} variant="filled">
            {alertMessage}
            <IconButton
              sx={{ p: 0.25 }}
              onClick={handleSnackbarAlertClose}
            >
              <CloseIcon />
            </IconButton>
          </Alert>
        </Snackbar>
      </div>
    );
  };

  const handleSnackbarAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertMessage('');
    setAlertType('');

    setNewPAFormOpen(false);
    setAddingPreAuth(false);
    setPAFormData({
      DDDID: '',
      memberMedicaid: '',
      providerMedicaid: '',
      priorAuth: '',
      procedureCode: '',
      startDate: moment(),
      endDate: moment(),
      authUnits: '',
      authAmount: '',
    });
  };

  useEffect(() => {
    async function fetchTaskForms() {
      try {
        const response = await axios.get(APIURL + `/customer_form/task/${selectedTaskID}`)
        setTaskForms(response.data);
      } catch (error) {
        console.log(error);
      }
    }

    if (selectedTaskID) {
      fetchTaskForms();
    }
  }, [selectedTaskID]);

  useEffect(() => {
    if (taskForms[0]?.tas_claim_id) {
      setClaimStatusLoading(true);
      axios.get(APIURL + `/claims?claim_id=${taskForms[0].tas_claim_id}`)
        .then(res => setClaimStatus(res.data.Status))
        .catch(() => setClaimStatus(null))
        .finally(() => setClaimStatusLoading(false));
    } else {
      setClaimStatus(null);
    }
  }, [taskForms]);

  const onDateChange = (direction) => {
    if (dateRangeView === 'week') {
      const currentWeek = dayjs(fromDate);

      setFromDate(currentWeek.add(direction === 'next' ? 1 : -1, "week").startOf("week").toDate());
      setToDate(currentWeek.add(direction === 'next' ? 1 : -1, "week").endOf("week").toDate());
    } else {
      const currentMonth = dayjs(fromDate);

      setFromDate(currentMonth.add(direction === 'next' ? 1 : -1, "month").startOf("month").toDate());
      setToDate(currentMonth.add(direction === 'next' ? 1 : -1, "month").endOf("month").toDate());
    }
  };

  const getSelectButton = () => {
    const checkClaimStatus = (tasks) => {
      return tasks ? (tasks[0] ? (tasks[0].tas_preAuth_id ? 'Sent to Claims' : '') : '') : '';
    };

    return (
      <div style={{ marginTop: 15, marginLeft: 10, width: "45%" }}>
        <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
          <Button
            variant="contained"
            fullWidth={true}
            onClick={() => { setSelectValidClaims(true); }}
            disabled={isLoading || claimsList.filter(
              (claim) =>
                (primaryBusinessType === 101 ? ((claim.tas_mm_status === 'Approved' || claim.ChargeIdentified === 'Customer added') && claim.priorAuth && !claim.tas_preAuth_id) : (claim.taskIds && claim.priorAuth && checkClaimStatus(claim.tasks) !== 'Sent to Claims' && !isFutureDate(getLatestTaskEndDate(claim.tasks)))) &&
                moment(claim.startDate).utc().month() === moment(fromDate).month() &&
                moment(claim.startDate).utc().year() === moment(fromDate).year() &&
                claim.cus_name
            ).length === 0}
          >
            Select Fileable Claims
          </Button>
        </Box>
      </div>
    );
  };

  const getUnselectButton = () => {
    return (
      <div style={{ marginTop: 15, marginLeft: 10, width: "35%" }}>
        <Box display="flex">
          <Button
            variant="contained"
            sx={{ width: "80%" }}
            onClick={() => {
              setSelectValidClaims(false);
              setSelectionModel({ type: 'include', ids: new Set() });
            }}
            style={{
              backgroundColor: "white",
              color: "grey"
            }}
          >
            Unselect All
          </Button>
        </Box>
      </div>
    );
  };

  const getSendButton = () => {
    return (
      <div style={{ marginTop: 15, width: "10%" }}>
        <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
          <Button
            variant="contained"
            onClick={() => { setShowModal(!showModal); }}
            color="success"
          >
            Send
          </Button>
        </Box>
      </div>
    );
  };

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  };

  const handleSaveSettings = async () => {
    let settings = apiRef.current.exportState();
    settings.columnVisibilityModel = colVisModel;
    settings.density = density;
    settings = JSON.stringify(settings);

    let saveGridSettings = await axios.put(`${APIURL}/datagrid`, { user_id: props.user.emp_id, report_id: gridId, settings: settings });

    console.log(saveGridSettings);
  };

  const handleColumnOrderChange = (params) => {
    let newOrder = [];
    setOrderedColumns((prevOrderedColumns) => {
      if (prevOrderedColumns.length === 0) {
        return apiRef.current.exportState().columns.orderedFields;
      }
      const newOrderedColumns = [...prevOrderedColumns];
      const oldIndex = params.oldIndex;
      const targetIndex = params.targetIndex;
      const oldColumn = prevOrderedColumns[oldIndex];
      newOrderedColumns.splice(oldIndex, 1);
      newOrderedColumns.splice(targetIndex, 0, oldColumn);
      newOrder = newOrderedColumns;
      return newOrderedColumns;
    });
    setInitialSettings((prevSettings) => {
      return {
        ...prevSettings,
        columns: {
          ...prevSettings.columns,
          orderedFields: newOrder.length !== 0 ? newOrder : apiRef.current.exportState().columns.orderedFields,
        }
      }
    })
  };

  const handleCellDoubleClick = (params) => {
    if (params.field === "billingNote") {
      setEditNote(true);
      setEditRow(params.row);
      setNoteText(params.row.billingNote);
    }
  };

  const updateBillingNote = async (b_id, note) => {
    try {
      if (note && note.replace(/\s/g, '').length !== 0) {
        await axios.put(APIURL + "/accountsReceivable/billingNote", {
          billing_note_id: b_id,
          billing_note: note,
          empID: props.user.emp_id,
        });

        setAlertMessage('Note successfully saved.');
        setAlertType('success');
      } else {
        setAlertMessage('Note cannot be empty');
        setAlertType('error');
      }
    } catch (error) {
      setAlertMessage(error.message);
      setAlertType('error');
    }

    setEditNote(false);
    setEditRow(null);
  };

  const addBillingNote = async (pa_id, note) => {
    try {
      if (note && note.replace(/\s/g, '').length !== 0) {
        await axios.post(APIURL + "/accountsReceivable/billingNote", {
          preAuth_id: pa_id,
          billing_note: note,
          empID: props.user.emp_id,
        });

        setAlertMessage('Note successfully saved.');
        setAlertType('success');
      } else {
        setAlertMessage('Note cannot be empty');
        setAlertType('error');
      }
    } catch (error) {
      setAlertMessage(error.message);
      setAlertType('error');
    }
    setEditNote(false);
    setEditRow(null);
  };

  const getConfirmDialog = () => {
    return (
      <Dialog open={showModal} onClose={() => setShowModal(!showModal)}>
        <DialogTitle sx={{ textAlign: 'center' }}>SUBMIT CLAIMS</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography>
            You have selected {selectionModel.ids.size} claims.
          </Typography>
          <Typography>
            Are you sure you want to send the selected claims?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', padding: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setShowModal(!showModal)}
          >
            Back
          </Button>
          <Box style={{ width: "30px" }}></Box>
          <Button
            variant="contained"
            onClick={() => { sendToClaims(); }}
            color="success"
            disabled={isAPILoading}
          >
            {isAPILoading ? "Sending..." : "Yes"}
            {isAPILoading && <CircularProgress size={24} sx={{ marginLeft: 2 }} />}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const getMTDialog = () => {
    return (
      <Dialog open={showMT} onClose={handleCloseMT} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>{mt.mt_type === "Monthly" ? "Monthly" : "Quarterly/Annual"} Monitoring Tool</Typography>
        </DialogTitle>
        <DialogContent>
          {mt?.mt_type === "Monthly" ?
            <div>
              <MonthlyTool
                data={mt?.data ? JSON.parse(mt.data) : {}}
                expand={true}
                read={true}
                onChange={() => { return }}
              />
              {mt?.mt_type === "Monthly" &&
                <Box sx={{ overflow: 'hidden', height: 0 }}>
                  <MonthlyTool
                    data={mt?.data ? JSON.parse(mt.data) : {}}
                    onChange={() => { return }}
                    ref={ref}
                    expand={true}
                  />
                </Box>
              }
            </div> :
            <div >
              <QuarterlyAnnualTool
                data={mt?.data ? JSON.parse(mt.data) : {}}
                expand={true}
                read={true}
                onChange={() => { return }}
              />
              {mt?.mt_type === "Quarterly" &&
                <Box sx={{ overflow: 'hidden', height: 0 }}>
                  <QuarterlyAnnualTool
                    data={mt?.data ? JSON.parse(mt.data) : {}}
                    onChange={() => { return }}
                    ref={ref}
                    expand={true}
                  />
                </Box>
              }
            </div>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMT}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const getTaskFormsDialog = () => {
    return (
      <Dialog
        open={selectedTaskID ? true : false}
        onClose={() => setSelectedTaskID(null)}
        maxWidth="md"
        fullWidth
        scroll='body'
        disableRestoreFocus
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderBottom: '1px solid #e0e0e0'
        }}>
          Forms

          {taskForms.length ?
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {taskForms[0]?.tas_title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {convertTime(taskForms[0]?.empLocdate, taskForms[0]?.empLocate_timezone, moment.tz.guess()) ?? moment(taskForms[0]?.empLocdate).utc().format('MM/DD/YYYY hh:mm A')} - {convertTime(taskForms[0]?.checkoutEmpDate, taskForms[0]?.empLocate_timezone, moment.tz.guess()) ?? moment(taskForms[0]?.checkoutEmpDate).utc().format('MM/DD/YYYY hh:mm A')}
              </Typography>
              {taskForms[0]?.tas_claim_id && (
                claimStatusLoading
                  ? <CircularProgress size={18} />
                  : claimStatus && (
                    <Chip
                      label={`Billing Status: ${claimStatus}`}
                      size="small"
                      color="success"
                      sx={{ mt: 0.5 }}
                    />
                  )
              )}
            </Box> : ''
          }
        </DialogTitle>
        <DialogContent>
          {taskForms.length === 0 ? (
            <Typography>No forms available for this task.</Typography>
          ) : (
            <Box>
              {taskForms.map((form, idx) => (
                <Button
                  key={form.form_name || idx}
                  variant="outlined"
                  sx={{ m: 1, width: '100%', justifyContent: 'flex-start' }}
                  onClick={() => {
                    setSelectedForm(form);
                    setOpenFormDialog(true);
                  }}
                >
                  {form.form_name || `Form ${idx + 1}`}
                </Button>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const getFormDialog = () => {
    return (
      <Dialog
        open={openFormDialog}
        onClose={() => setOpenFormDialog(false)}
        maxWidth="md"
        fullWidth
        scroll='body'
        disableRestoreFocus
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderBottom: '1px solid #e0e0e0'
        }}>
          {selectedForm?.form_name || "Form"}
        </DialogTitle>
        <DialogContent>
          {selectedForm && (
            <Form
              form={JSON.parse(selectedForm?.translated_json_schema ? selectedForm?.translated_json_schema : selectedForm?.json_schema)}
              submission={JSON.parse(selectedForm?.data)}
              options={{
                readOnly: true,
                hide: {
                  submit: true,
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const getNoteDialog = () => {
    return (
      <Dialog open={editNote} onClose={() => { setEditNote(false); setEditRow(null); }} maxWidth="lg" fullWidth>
        <DialogTitle>{editRow.billingNoteId ? 'Edit Note' : 'Add Note'}</DialogTitle>
        <DialogContent>
          <div>
            <TextField
              multiline
              value={noteText}
              onChange={(event) => {
                setNoteText(event.target.value);
              }}
              style={{ margin: '5px', width: '100%' }}
              inputProps={{ maxLength: 2000 }}
              minRows={8}
              label="Note"
              variant="outlined"
              id="bNote"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            sx={{
              width: '100%',
              padding: '5px',
              margin: '5px',
            }}
            onClick={() => editRow.billingNoteId ? updateBillingNote(editRow.billingNoteId, noteText) : addBillingNote(editRow.primaryID, noteText)}
          >
            {editRow.billingNoteId ? 'Edit Note' : 'Add Note'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const getExpectedAmountTotal = (selectedRows) => {
    const total = selectedRows.reduce((sum, row) => {
      const amount = primaryBusinessType === 101 ? row.authAmount : row.expectedAmount;
      const val = amount ? parseFloat(amount) : 0;
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

    return total;
  };

  const getPAforSC = () => {
    let SC_columns = [
      { field: "id", headerName: "ID", width: 50, },
      { field: "primaryID", headerName: "Primary ID", width: 50, },
      { field: 'ChargeIdentified', headerName: 'Charge Identified', width: 125, },
      { field: "DDDID", headerName: "DDDID", width: 80, },
      { field: "PatientName", headerName: "Patient Name", width: 150, },
      { field: "DateOfBirth", headerName: "DOB", width: 100, },
      { field: "Gender", headerName: "Gender", width: 65, },
      { field: "DDD_tier", headerName: "DDD Tier", width: 65, },
      { field: "memberMedicaid", headerName: "Medicaid ID", width: 120, },
      { field: "priorAuth", headerName: "Prior Auth", width: 110, },
      { field: "procedureCode", headerName: "Proc Code", width: 100, },
      { field: "procedureCodeModifier", headerName: "Proc Code Modifier", width: 100, },
      { field: "DiagnosisCode", headerName: "Diagnosis Code", width: 90, },
      { field: "startDate", headerName: "Task Start Date", width: 100, },
      { field: "endDate", headerName: "Task End Date", width: 100, },
      { field: "authUnits", headerName: "Auth Units", width: 50, },
      { field: "authAmount", headerName: "Auth Amount", width: 70, align: "right", },
      { field: "task_id", headerName: "Task ID", width: 120, },
      {
        field: "tas_title",
        headerName: "Task Title",
        width: 120,
        renderCell: (params) => {
          return (
            <Tooltip title={params.value}>
              <div class="crop">{params.value}</div>
            </Tooltip>
          )
        },
      },
      { field: "tas_type", headerName: "Task Type", width: 120, },
      { field: "tas_start_date", headerName: "Task Date", width: 120, },
      { field: "taskStatus", headerName: "Status", width: 120, },
      { field: "Status", headerName: "Claim Status", width: 120, },
      {
        field: "tas_id",
        headerName: "MMT",
        width: 70,
        renderCell: (params) => {
          const onClick = async () => {
            const mm = await axios.get(APIURL + "/monthlymonitoring/" + params.value);
            if (mm.data !== '') {
              const mmt = (mm.data[0]);
              setMT(mmt);
            }
          }
          return (
            <div>
              {!!params.value &&
                <div style={{ color: '#3FA7D6', padding: '10px' }}>
                  <Link href="#" underline="hover" onClick={onClick}>
                    <i class="far fa-sticky-note" style={{ marginRight: '5px' }}></i>
                    View
                  </Link>
                </div>
              }
              {Object.keys(params.row).length !== 0 && !params.value &&
                <div style={{ color: '#3FA7D6', padding: '10px' }}>
                  <Link style={{ textDecoration: 'none', color: 'grey', cursor: 'auto' }} href="#">
                    <i class="far fa-sticky-note" style={{ marginRight: '5px' }}></i>
                    View
                  </Link>
                </div>}
            </div>
          )
        }
      },
      { field: "mt_status", headerName: "MT Status", width: 150, },
      { field: "errCode", headerName: "Error Code", width: 120, },
      { field: "errDescription", headerName: "Error Description", width: 140, },
      {
        field: "billingNote",
        headerName: "Notes",
        width: 300,
        editable: true,
        renderCell: (params) => {
          return (
            <Tooltip title={params.value}>
              <div class="crop">{params.value}</div>
            </Tooltip>
          );
        },
      },
    ]

    if (initialSettings.columns) {
      SC_columns = SC_columns.sort((a, b) => {
        const indexA = initialSettings.columns.orderedFields.indexOf(a.field);
        const indexB = initialSettings.columns.orderedFields.indexOf(b.field);

        if (indexA === -1 && indexB === -1) return 0;

        if (indexA === -1) return 1;

        if (indexB === -1) return -1;

        return indexA - indexB;
      });
    }

    if (initialSettings?.columns?.dimensions) {
      Object.keys(initialSettings.columns.dimensions).forEach((field) => {
        try {
          const colIndex = SC_columns.findIndex(col => col.field === field);
          if (colIndex !== -1) {
            SC_columns[colIndex].width = initialSettings.columns.dimensions[field].width;
          }
        } catch (e) {
          console.log('ERROR', e);
        }
      });
    }

    return (
      <div style={{ height: 'calc(100vh - 225px)', width: '100%' }}>
        <DataGridPro
          apiRef={apiRef}
          onColumnOrderChange={handleColumnOrderChange}
          density={density}
          onDensityChange={(newDensity) => setDensity(newDensity)}
          columnVisibilityModel={colVisModel}
          onColumnVisibilityModelChange={(newModel) => setColVisModel(newModel)}
          initialState={initialSettings.columns ? initialSettings : { sorting: { sortModel: [{ field: "mt_status", sort: "desc" }, { field: "priorAuth", sort: "desc" }] } }}
          onRowSelected={(params) => console.log(params)}
          checkboxSelection={true}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setSelectionModel(newRowSelectionModel);
            const selectedRows = formattedRows.filter(r => newRowSelectionModel.ids.has(r.id));
            setExpectedAmountTotal(getExpectedAmountTotal(selectedRows));
          }}
          rows={formattedRows}
          columns={SC_columns}
          slots={{
            toolbar: () => {
              return (
                <DefaultToolbar>
                  <Tooltip title="Upload Prior Auth CSV">
                    <ToolbarButton onClick={() => setCsvAdd(!csvAdd)} disabled={false}>
                      <UploadFileIcon />
                    </ToolbarButton>
                  </Tooltip>
                  <Tooltip title="Add an entry">
                    <ToolbarButton onClick={() => setNewPAFormOpen(true)} disabled={false}>
                      <AddIcon />
                    </ToolbarButton>
                  </Tooltip>
                  <SaveViewButton onClick={debounce(handleSaveSettings, 500)} />
                </DefaultToolbar>
              );
            },
            noRowsOverlay: () => (
              <Box sx={{ height: "100%", display: 'flex', alignItems: "center", margin: 'auto', justifyContent: "center" }}>
                <Typography>No Prior Authorizations for this month in the system</Typography>
              </Box>
            ),
          }}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
              textAlign: 'left',
              wordBreak: 'break-word',
              maxWidth: '100%',
            },
          }}
          isRowSelectable={(params) => (params.row.PatientName && (params.row.mt_status === "Approved" || params.row.ChargeIdentified === 'Customer added') && params.row.priorAuth && params.row.Status !== 'Sent to Claims')}
          onCellDoubleClick={(params, event) => { handleCellDoubleClick(params); }}
          disableRowSelectionOnClick
          loading={isLoading}
          showToolbar
          treeData={props.tabKey === 'changeCapture' ? true : false}
          getTreeDataPath={(row) => row.hierarchy}
        />
      </div>
    );
  };

  const getPAforSP = () => {
    let SP_columns = [
      { field: "id", headerName: "ID", width: 50, },
      { field: "primaryID", headerName: "Primary ID", width: 50, },
      { field: 'ChargeIdentified', headerName: 'Charge Identified', width: 125, },
      { field: "DDDID", headerName: "DDDID", width: 80, },
      { field: "PatientName", headerName: "Patient Name", width: 150, },
      { field: "DateOfBirth", headerName: "DOB", width: 100, },
      { field: "Gender", headerName: "Gender", width: 65, },
      { field: "DDD_tier", headerName: "DDD Tier", width: 65, },
      { field: "memberMedicaid", headerName: "Medicaid ID", width: 120, },
      { field: "priorAuth", headerName: "Prior Auth", width: 110, },
      { field: "procedureCode", headerName: "Proc Code", width: 100, },
      { field: "procedureCodeModifier", headerName: "Proc Code Modifier", width: 100, },
      { field: "DiagnosisCode", headerName: "Diagnosis Code", width: 90, },
      { field: "startDate", headerName: "Task Start Date", width: 100, },
      { field: "endDate", headerName: "Task End Date", width: 100, },
      { field: "authUnits", headerName: "Auth Units", width: 50, },
      { field: "authAmount", headerName: "Auth Amount", width: 70, align: "right", },
      { field: "expectedAmount", headerName: "Expected Amount", width: 70, align: "right", },
      { field: "billingRate", headerName: "Billing Rate", width: 70, align: "right", },
      { field: "taskStatus", headerName: "Status", width: 120, },
      {
        field: "tas_ids",
        headerName: "Forms/Task IDs",
        width: 120,
        renderCell: (params) => {
          let ids = params.value?.split(',').map(id => id.trim()).filter(Boolean) || [];

          return (
            <div>
              {ids.map((id, idx) => (
                <Link
                  key={id}
                  href={`#`}
                  underline="hover"
                  onClick={(e) => {
                    setSelectedTaskID(id);
                  }}
                  sx={{ marginRight: 1 }}
                >
                  {id}
                </Link>
              ))}
            </div>
          );
        },
      },
      { field: "tas_type", headerName: "Task Type", width: 120, },
      { field: "cumulativeUnits", headerName: "Units Used", width: 55, },
      { field: "totalRemainingUnits", headerName: "Total Units Remaining", width: 80, },
      { field: "remainingUnits", headerName: "Units Remaining", width: 80, },
      { field: "Status", headerName: "Claim Status", width: 120, },
      { field: "errCode", headerName: "Error Code", width: 120, },
      { field: "errDescription", headerName: "Error Description", width: 140, },
      {
        field: "billingNote",
        headerName: "Notes",
        width: 300,
        editable: true,
        renderCell: (params) => {
          return (
            <Tooltip title={params.value}>
              <div class="crop">{params.value}</div>
            </Tooltip>
          );
        },
      },
      { field: "isNewPlan", headerName: "Is New Plan", width: 100, },
      { field: "serviceType", headerName: "Service Type", width: 100, },
      { field: "projectEndsOn", headerName: "Project Ends On", width: 100, },
    ]

    if (initialSettings.columns) {
      SP_columns = SP_columns.sort((a, b) => {
        const indexA = initialSettings.columns.orderedFields.indexOf(a.field);
        const indexB = initialSettings.columns.orderedFields.indexOf(b.field);

        if (indexA === -1 && indexB === -1) return 0;

        if (indexA === -1) return 1;

        if (indexB === -1) return -1;

        return indexA - indexB;
      });
    }

    if (initialSettings?.columns?.dimensions) {
      Object.keys(initialSettings.columns.dimensions).forEach((field) => {
        try {
          const colIndex = SP_columns.findIndex(col => col.field === field);
          if (colIndex !== -1) {
            SP_columns[colIndex].width = initialSettings.columns.dimensions[field].width;
          }
        } catch (e) {
          console.log('ERROR', e);
        }
      });
    }

    return (
      <div style={{ height: 'calc(100vh - 225px)', width: '100%' }}>
        <DataGridPro
          apiRef={apiRef}
          onColumnOrderChange={handleColumnOrderChange}
          density={density}
          onDensityChange={(newDensity) => setDensity(newDensity)}
          columnVisibilityModel={colVisModel}
          onColumnVisibilityModelChange={(newModel) => setColVisModel(newModel)}
          initialState={initialSettings.columns ? initialSettings : { sorting: { sortModel: [{ field: "taskStatus", sort: "desc" }, { field: "startDate", sort: "desc" }] } }}
          onRowSelected={(params) => console.log(params)}
          checkboxSelection={true}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setSelectionModel(newRowSelectionModel);
            const selectedRows = formattedRows.filter(r => newRowSelectionModel.ids.has(r.id));
            setExpectedAmountTotal(getExpectedAmountTotal(selectedRows));
          }}
          rows={formattedRows}
          columns={SP_columns}
          slots={{
            toolbar: () => {
              return (
                <DefaultToolbar>
                  <Tooltip title="Upload Prior Auth CSV">
                    <ToolbarButton onClick={() => setCsvAdd(!csvAdd)} disabled={false}>
                      <UploadFileIcon />
                    </ToolbarButton>
                  </Tooltip>
                  <Tooltip title="Add an entry">
                    <ToolbarButton onClick={() => setNewPAFormOpen(true)} disabled={false}>
                      <AddIcon />
                    </ToolbarButton>
                  </Tooltip>
                  <SaveViewButton onClick={debounce(handleSaveSettings, 500)} />
                </DefaultToolbar>
              );
            },
            noRowsOverlay: () => (
              <Box sx={{ height: "100%", display: 'flex', alignItems: "center", margin: 'auto', justifyContent: "center" }}>
                <Typography>No Prior Authorizations for this month in the system</Typography>
              </Box>
            ),
          }}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
              textAlign: 'left',
              wordBreak: 'break-word',
              maxWidth: '100%',
            },
          }}
          isRowSelectable={(params) => params.row.PatientName && params.row.tas_ids && params.row.priorAuth && params.row.Status !== 'Sent to Claims' && !isFutureDate(params.row.latestTaskEndDate)}
          onCellDoubleClick={(params, event) => { handleCellDoubleClick(params); }}
          disableRowSelectionOnClick
          loading={isLoading}
          showToolbar
          treeData={props.tabKey === 'changeCapture' ? true : false}
          getTreeDataPath={(row) => row.hierarchy}
        />
      </div>
    );
  };

  return (
    <div style={{ display: "flex", backgroundColor: "#fafafa" }}>
      <div
        style={{
          display: "flex",
          flex: "0 0 auto",
          flexDirection: "column",
          justifyContent: "flex-start",
          marginTop: 25,
          width: "100%"
        }}
      >
        <div style={{ display: "flex", flexDirection: "row", width: "100%", marginTop: -30, marginBottom: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", marginTop: 15, width: "100%" }}>
            <Button
              size="large"
              variant="contained"
              onClick={() => onDateChange('previous')}
            >
              <i className="fas fa-arrow-left"></i>
            </Button>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateRangePicker
                // label='Date Range'
                value={[dayjs(fromDate), dayjs(toDate)]}
                onChange={(newValue) => {
                  setFromDate(newValue[0] ? new Date(newValue[0]) : null);
                  setToDate(newValue[1] ? new Date(newValue[1]) : null);
                }}
                slotProps={{
                  shortcuts: {
                    items: shortcutsItems,
                  },
                  textField: {
                    hiddenLabel: true,
                    size: 'small',
                    variant: 'filled',
                    style: { width: 275, marginLeft: 20, marginRight: 20, background: '#1976D2' },
                    sx: {
                      "& .MuiPickersInputBase-root": {
                        color: "white",
                      },
                      "& .MuiInputLabel-root": {
                        color: "white",
                      },
                      "&.Mui-focused .MuiInputLabel-root": {
                        color: "white",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "white",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <Button
              size="large"
              variant="contained"
              onClick={() => onDateChange('next')}
            >
              <i className="fas fa-arrow-right"></i>
            </Button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row', width: '100%', alignSelf: 'center' }}>
            {(selectValidClaims || selectionModel.ids.size !== 0) && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignSelf: 'center',
                  background: 'darkorange',
                  borderRadius: 999,
                  padding: 8,
                  marginRight: 10,
                  marginTop: 15,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  minWidth: 200,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                  Expected Amount: ${expectedAmountTotal?.toFixed(2)}
                </Typography>
              </div>
            )}
            {!selectValidClaims && selectionModel.ids.size === 0 && getSelectButton()}
            {(selectValidClaims || selectionModel.ids.size !== 0) && getUnselectButton()}
            {(selectValidClaims || selectionModel.ids.size !== 0) && getSendButton()}
          </div>
        </div>

        {primaryBusinessType === 101 && getPAforSC()}
        {[102, 105].includes(primaryBusinessType) && getPAforSP()}
      </div>

      {csvAdd && renderCSVModal()}
      {newPAFormOpen && addPreAuthForm()}
      {alertMessage && SnackbarComponent()}

      {showModal && getConfirmDialog()}
      {showMT && getMTDialog()}
      {selectedTaskID && getTaskFormsDialog()}
      {openFormDialog && getFormDialog()}
      {editNote && getNoteDialog()}
    </div>
  );
}